'use client';

/**
 * The listening room: a solved room, live, that you can hear.
 *
 * One Before/After drives three things at once: the room (the wipe sweeps
 * the bare room, drawn as the solver sees it, into the treated one), the
 * sound (the same song through each room's own impulse responses) and the
 * curve at the seat. Until someone picks a side it alternates on its own —
 * the room, the curve and, once playing, the sound — so the comparison makes
 * itself; a pick holds it, and picking the held side again lets it go.
 *
 * The three rooms are the showcase's (landing/showcase/showcaseTabs.ts), each
 * paired with the problem it is bought to solve, and the same rooms the
 * product's own pre-upsell shows. The room renders are live (`RoomWipe`,
 * the editor's SceneCanvas), so a drag turns the room.
 *
 * Dressing follows the hero's: the room floats on the stage with a pool of
 * light under it, and the controls are glass laid over the picture — never a
 * panel the picture sits inside. On a phone they drop under the room, where
 * they are docked, so there they are paper.
 */

import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { useAlternate, type ABState } from '@/components/upsell/BeforeAfter';
import { SHOWCASE_SLICES, type ShowcaseSlice } from '@/components/landing/showcase/showcaseTabs';
import { SELECTED_RAISED } from '@/components/room/chrome/selection';
import { buttonClass } from '@/components/ui/Button';
import { HOME_LISTEN, HOME_LISTEN_ROOMS } from '@/lib/copy/homepage';
import { roomAudioInputs } from './roomAudio';
import SeatCard from './SeatCard';
import { useListeningAudio, type ListeningAudio } from './useListeningAudio';

/** The showcase frame's own fill: the room as close as it gets unclipped. */
const ROOM_FILL = 0.98;

/** The surface of a control that floats over the room on a wide screen but
 *  drops into the page's flow under it on a phone: glass only where it
 *  floats (`md:`/`lg:rt-glass` at each use), paper where it is docked. */
const IN_FLOW_CARD = 'border border-[var(--app-border)] bg-[var(--app-card)]';

/** The room's frame, shared by the live wipe and the still that holds its
 *  place, so the swap moves nothing (CLS). */
const FRAME = 'aspect-[4/5] w-full sm:aspect-[16/10] lg:aspect-[16/9]';

/** The live room: the editor's scene, two canvases. Split out of the page's
 *  first bundle, and mounted only as the section comes near (below). */
const RoomWipe = dynamic(
  () => import('@/components/upsell/BeforeAfter').then((m) => m.RoomWipe),
  { ssr: false, loading: () => <RoomStill /> },
);

/** How long each side holds while the song plays — long enough to hear a
 *  room's decay before it changes. Silent, the picture alone keeps the
 *  showcase's quicker beat. */
const LISTENING_PERIOD_MS = 5200;

interface ListenRoom { slice: ShowcaseSlice; label: string }

const ROOMS: readonly ListenRoom[] = HOME_LISTEN_ROOMS.flatMap(({ goal, label }) => {
  const slice = SHOWCASE_SLICES.find((s) => s.goal === goal);
  return slice ? [{ slice, label }] : [];
});

/** A selection's geometry, sized for the front door. State comes from the
 *  editor's selection language (chrome/selection.ts): raised when chosen,
 *  never a hue — the accent stays on position in the flow. */
function optionClass(active: boolean, shape: string): string {
  return `${shape} border transition-colors duration-[var(--rt-fade-ms)] ${
    active
      ? SELECTED_RAISED
      : 'border-transparent text-[var(--ed-text-dim)] hover:text-[var(--ed-text)]'
  }`;
}

export default function ListeningRoom() {
  const [roomId, setRoomId] = useState(ROOMS[0].slice.id);
  const room = ROOMS.find((r) => r.slice.id === roomId) ?? ROOMS[0];
  const audio = useListeningAudio();
  const playing = audio.phase === 'playing';
  const ab = useAlternate(playing ? LISTENING_PERIOD_MS : undefined);
  const rootRef = useRef<HTMLDivElement>(null);

  // The sound follows the picture's side — whoever set it: a pick, or the
  // alternation. Syncing an external system to state is what effects are for.
  const { setState: setAudioState, pause } = audio;
  useEffect(() => { setAudioState(ab.state); }, [ab.state, setAudioState]);

  // Out of sight, out of earshot: a song still playing after the visitor has
  // scrolled on is a song they now have to hunt for.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([e]) => { if (!e.isIntersecting) pause(); }, { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, [pause]);

  // Two WebGL contexts and a scene each are not free, and most visitors
  // arrive at the top of the page: the live room waits until the section is
  // a screen away, and the still holds its place until then.
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setNear(true); io.disconnect(); }
    }, { rootMargin: '100% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const pickRoom = (next: ListenRoom) => {
    if (next.slice.id === roomId) return;
    setRoomId(next.slice.id);
    audio.setRoom(roomAudioInputs(next.slice.room));
  };

  return (
    <div ref={rootRef} data-testid="home-listen-room" className="guided-theme mx-auto mt-10 w-full max-w-[1180px] md:mt-12">
      <div role="group" aria-label="Room" className="flex flex-wrap justify-center gap-2">
        {ROOMS.map((r) => {
          const active = r.slice.id === roomId;
          return (
            <button
              key={r.slice.id}
              type="button"
              aria-pressed={active}
              data-testid={`home-listen-room-${r.slice.id}`}
              onClick={() => pickRoom(r)}
              className={optionClass(active, 'flex h-12 items-center gap-2 rounded-full px-4 text-body font-medium sm:h-14 sm:pl-2 sm:pr-5')}
            >
              {/* A phone row has room for three names, not three pictures. */}
              <Image
                src={`/home/room-${r.slice.id}.webp`}
                alt=""
                width={96}
                height={72}
                className="hidden h-9 w-12 object-contain sm:block"
              />
              {r.label}
            </button>
          );
        })}
      </div>

      {/* `isolate`: the light sits at z -1 and needs a stacking context here. */}
      <div className="relative isolate mt-8">
        <div aria-hidden className="listen-light pointer-events-none absolute inset-x-[6%] inset-y-[4%] -z-10" />
        {near ? (
          <RoomWipe
            key={room.slice.id}
            room={room.slice.room}
            fill={ROOM_FILL}
            reveal={ab.state}
            className={FRAME}
          />
        ) : (
          <RoomStill />
        )}

        <div className="mt-4 flex justify-center md:absolute md:bottom-6 md:left-1/2 md:mt-0 md:-translate-x-1/2">
          <Player audio={audio} room={room} state={ab.state} onPick={ab.pick} />
        </div>

        <SeatCard
          slice={room.slice}
          state={ab.state}
          className={`mx-auto mt-4 max-w-md lg:absolute lg:right-6 lg:top-6 lg:mt-0 lg:w-[300px] ${IN_FLOW_CARD} lg:rt-glass`}
        />
      </div>
    </div>
  );
}

/** The default room, treated, as a still: what the frame shows before the
 *  live room mounts — the hero's own render of the same theater. */
function RoomStill() {
  return (
    <div className={`relative flex items-center justify-center ${FRAME}`}>
      <Image
        src="/home/hero-room.webp"
        alt=""
        width={2400}
        height={1831}
        sizes="(min-width: 1024px) 1100px, 100vw"
        className="h-full w-auto max-w-full object-contain"
      />
    </div>
  );
}

/** Play, the side, and how far through the song: one glass bar. */
function Player({ audio, room, state, onPick }: {
  audio: ListeningAudio;
  room: ListenRoom;
  state: ABState;
  onPick: (s: ABState) => void;
}) {
  const { phase } = audio;
  const playing = phase === 'playing';
  const loading = phase === 'loading';

  return (
    <div data-testid="home-listen-player" className={`flex items-center gap-3 rounded-full p-2 pr-3 sm:pr-5 ${IN_FLOW_CARD} md:rt-glass`}>
      <button
        type="button"
        data-testid="home-listen-play"
        aria-label={playing ? HOME_LISTEN.pause : HOME_LISTEN.play}
        aria-busy={loading}
        onClick={() => (playing || loading ? audio.pause() : audio.play(roomAudioInputs(room.slice.room), state))}
        className={buttonClass({ variant: 'primary', size: 'md', className: '!h-12 !w-12 shrink-0 !px-0' })}
      >
        {loading ? <Spinner /> : playing ? <Pause className="h-5 w-5" fill="currentColor" /> : <Play className="ml-0.5 h-5 w-5" fill="currentColor" />}
      </button>

      <div role="group" aria-label="Treatment" className="flex rounded-full bg-[var(--ed-surface-2)] p-1">
        {(['before', 'after'] as const).map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={state === s}
            data-testid={`home-listen-${s}`}
            onClick={() => onPick(s)}
            className={optionClass(state === s, 'h-10 rounded-full px-4 text-body font-medium')}
          >
            {s === 'before' ? HOME_LISTEN.before : HOME_LISTEN.after}
          </button>
        ))}
      </div>

      {phase === 'failed' ? (
        <span role="status" className="hidden text-body-sm text-[var(--rt-ink-dim)] sm:block">{HOME_LISTEN.failed}</span>
      ) : (
        <Progress subscribe={audio.subscribeProgress} />
      )}
    </div>
  );
}

/** How far through the song — a readout, drawn by the engine's own clock
 *  straight onto the bar, so playback never re-renders the room. */
function Progress({ subscribe }: { subscribe: ListeningAudio['subscribeProgress'] }) {
  const fill = useRef<HTMLSpanElement>(null);
  useEffect(
    () => subscribe((f) => { if (fill.current) fill.current.style.transform = `scaleX(${f})`; }),
    [subscribe],
  );
  return (
    <span aria-hidden className="relative hidden h-1 w-24 overflow-hidden rounded-full bg-[var(--rt-hairline)] sm:block">
      <span ref={fill} className="absolute inset-0 origin-left bg-[var(--rt-ink)]" style={{ transform: 'scaleX(0)' }} />
    </span>
  );
}

function Spinner() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 animate-spin motion-reduce:animate-none" aria-hidden>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2.5" />
      <path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}
